"use client";
import { Fragment, useState, useMemo } from "react";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function CountrySelectorGenerator({
  data,
  handleMenuItemClick,
  value,
}) {
  const [searchQuery, setSearchQuery] = useState("");

  // Sort countries A-Z and filter based on search query
  const filteredCountries = useMemo(() => {
    // First, sort countries alphabetically by country_name
    const sortedCountries = [...(data || [])].sort((a, b) => {
      const nameA = (a.country_name || "").toLowerCase();
      const nameB = (b.country_name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });

    // Then filter if there's a search query
    if (!searchQuery.trim()) {
      return sortedCountries;
    }

    const query = searchQuery.toLowerCase().trim();
    return sortedCountries.filter((country) => {
      const countryName = country.country_name?.toLowerCase() || "";
      const alphaCode = country.country_alpha_code?.toLowerCase() || "";
      return countryName.includes(query) || alphaCode.includes(query);
    });
  }, [data, searchQuery]);

  return (
    <Menu as="div" className="w-full relative inline-block text-left">
      <div>
        <Menu.Button className="inline-flex w-full justify-between items-center gap-x-1.5 rounded-xl bg-white px-4 py-3 text-base font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-all">
          <span className="flex items-center gap-2">
            {value ? (
              <>
                <span>{value.country_flag}</span>
                <span>{value.country_name}</span>
              </>
            ) : (
              <span className="text-gray-500">Select Country</span>
            )}
          </span>
          <ChevronDownIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="max-h-[50vh] h-auto overflow-y-scroll p-2 absolute left-0 z-50 mt-2 w-full origin-top-right rounded-2xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none will-change-transform">
          <div className="py-1">
            <div className="px-4 py-2.5 text-xs font-bold text-gray-700 uppercase tracking-wider bg-gray-100 rounded-lg mb-2">
              🗺️ Countries
            </div>
            
            {/* Search input */}
            <div className="px-2 mb-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search country..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#090EDB] focus:border-transparent"
                />
              </div>
            </div>

            {/* Countries list */}
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <Menu.Item key={country?.id}>
                  {({ active }) => (
                    <button
                      onClick={() => {
                        handleMenuItemClick(country);
                        setSearchQuery(""); // Reset search after selection
                      }}
                      className={classNames(
                        active ? "bg-gray-100" : "",
                        "text-gray-700 block px-4 py-2 my-1 text-sm w-full text-left rounded-lg hover:bg-gray-50 transition-colors"
                      )}
                    >
                      {country?.country_flag} {country?.country_name}
                    </button>
                  )}
                </Menu.Item>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                No countries found matching &quot;{searchQuery}&quot;
              </div>
            )}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
